import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Package, Truck, CheckCircle, AlertCircle, ClipboardList, ArrowRight, RefreshCw, AlertTriangle, Clock } from "lucide-react";
import { useHasPerm } from "@/context/AuthContext";

/**
 * Operaciones — panel para roles con permiso de despacho/gestión.
 * Enfoque: qué hay que confirmar, despachar y entregar HOY.
 */
export default function OperacionesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const canDecideExc = useHasPerm("excepciones.aprobar");
  // El KPI de Excepciones ahora solo aparece para quien puede aprobarlas.
  // La vendedora ve el estado inline dentro de su propio pedido.
  const canVerExc = canDecideExc;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/operaciones");
      setData(data);
    } catch (e) { console.error("OperacionesPage: no se pudo cargar", e); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 45000);
    return () => clearInterval(t);
  }, []);

  if (loading || !data) return (
    <div className="h-96 flex items-center justify-center text-muted-foreground">Cargando operaciones...</div>
  );

  const k = data.kpis;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary/90">Operaciones</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 text-foreground" style={{ fontStyle: "italic" }}>Sala de control.</h1>
          <p className="text-muted-foreground mt-1">Todo lo que pasa por sus manos, en un solo lugar.</p>
        </div>
        <button onClick={load} className="h-10 px-4 rounded-full border border-border text-sm hover:bg-accent transition-all" data-testid="ops-refresh">
          <RefreshCw className="w-3.5 h-3.5 inline mr-1.5" /> Actualizar
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* NOTA (Mar 14 2026): quitamos KPI "Por confirmar" (borradores) para operadores.
            Los borradores son propiedad del vendedor; el operador solo trabaja con
            pedidos confirmados en adelante. */}
        {canVerExc && (
          <OpKPI title="Excepciones" value={k.excepciones_pendientes} icon={AlertCircle} accent="amber" onClick={() => navigate("/excepciones")} testid="ops-kpi-exc" />
        )}
        <OpKPI title="En cola despacho" value={k.en_cola_despacho} icon={Package} accent="primary" onClick={() => navigate("/pedidos?estado=confirmado")} testid="ops-kpi-despacho" />
        <OpKPI title="En ruta" value={k.en_ruta} icon={Truck} accent="slate" onClick={() => navigate("/pedidos?estado=despachado")} testid="ops-kpi-ruta" />
        <OpKPI title="Entregados hoy" value={k.entregados_hoy} icon={CheckCircle} accent="emerald" testid="ops-kpi-entregados" />
      </div>

      {/* Cola despacho + En ruta */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OpList
          title="Cola de despacho" subtitle="Confirmados listos para preparar" icon={Package}
          items={data.cola_despacho} empty="Nada por despachar."
          renderItem={(p) => ({ primary: p.cliente_nombre, secondary: `#${p.numero} · ${p.ciudad || "sin ciudad"}`, value: formatCOP(p.total), timestamp: p.confirmed_at })}
          onItemClick={(p) => navigate("/pedidos")}
          accent="primary"
        />
        <OpList
          title="En ruta" subtitle="Despachados esperando entrega" icon={Truck}
          items={data.en_ruta || []} empty="Nada en tránsito."
          renderItem={(p) => ({ primary: p.cliente_nombre, secondary: `#${p.numero} · ${p.guia?.transportadora || "sin transportadora"}`, value: formatCOP(p.total), timestamp: p.dispatched_at })}
          onItemClick={(p) => navigate("/pedidos")}
          accent="slate"
        />
      </div>

      {/* En ruta + Stock alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OpList
          title="En ruta" subtitle="Despachados esperando entrega" icon={Truck}
          items={data.en_ruta} empty="Nada en tránsito."
          renderItem={(p) => ({ primary: p.cliente_nombre, secondary: `#${p.numero} · ${p.ciudad || ""}`, value: formatCOP(p.total), timestamp: p.dispatched_at })}
          onItemClick={(p) => navigate("/pedidos")}
        />

        <div className="p-6 rounded-3xl bg-amber-50/50 dark:bg-amber-500/5 border border-amber-200/50">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <div className="text-xs uppercase tracking-[0.16em] text-amber-700 font-semibold">Stock crítico</div>
          </div>
          <h3 className="font-display text-2xl mt-1" style={{ fontStyle: "italic" }}>Productos con menos de 3 unidades</h3>
          {data.stock_alertas.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Stock en niveles saludables.</div>
          ) : (
            <div className="mt-3 space-y-2">
              {data.stock_alertas.slice(0, 6).map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-card">
                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-semibold text-sm tabular-nums">
                    {s.stock_actual}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.nombre}</div>
                    <div className="text-[10px] text-muted-foreground">{s.codigo || s.sku} · Mínimo: {s.stock_minimo || 0}</div>
                  </div>
                  <button onClick={() => navigate("/productos")} className="text-[10px] text-primary hover:underline">
                    Ver
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OpKPI({ title, value, icon: Icon, accent = "slate", onClick, testid }) {
  const bg = {
    primary: "bg-primary/10 border-primary/20 text-primary",
    amber: "bg-amber-50 dark:bg-amber-500/5 border-amber-200/50 text-amber-700",
    blue: "bg-blue-50 dark:bg-blue-500/5 border-blue-200/50 text-blue-700",
    emerald: "bg-emerald-50 dark:bg-emerald-500/5 border-emerald-200/50 text-emerald-700",
    slate: "bg-card border-border text-foreground",
  }[accent] || "bg-card border-border";
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`p-3 md:p-4 rounded-2xl border text-left transition-all overflow-hidden ${onClick ? "hover:-translate-y-[1px] btn-tactile" : ""} ${bg}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <Icon className="w-4 h-4 opacity-70 flex-shrink-0" strokeWidth={1.8} />
        {value > 0 && accent === "amber" && <div className="w-2 h-2 rounded-full bg-amber-500 pulse-dot" />}
      </div>
      <div className="text-2xl md:text-3xl font-semibold mt-2 md:mt-3 tabular-nums" style={{ overflowWrap: "anywhere" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.12em] font-semibold mt-1 opacity-80 leading-tight">{title}</div>
    </button>
  );
}

function OpList({ title, subtitle, icon: Icon, items, empty, renderItem, onItemClick, accent }) {
  return (
    <div className={`p-6 rounded-3xl border ${accent === "primary" ? "bg-primary/5 border-primary/20" : "bg-card border-border"}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${accent === "primary" ? "text-primary" : "text-muted-foreground"}`} />
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">{title}</div>
      </div>
      <h3 className="font-display text-xl mt-1" style={{ fontStyle: "italic" }}>{subtitle}</h3>
      {items.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">{empty}</div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.slice(0, 5).map((it, i) => {
            const r = renderItem(it);
            return (
              <button key={it.id || i} onClick={() => onItemClick && onItemClick(it)} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-accent/40 transition-colors text-left" data-testid={`op-item-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{r.primary}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{r.secondary}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold tabular-nums">{r.value}</div>
                  {r.timestamp && (
                    <div className="text-[10px] text-muted-foreground flex items-center justify-end gap-0.5"><Clock className="w-2.5 h-2.5" />{r.timestamp.slice(5, 16).replace("T", " ")}</div>
                  )}
                </div>
                <ArrowRight className="w-3 h-3 text-muted-foreground/40" />
              </button>
            );
          })}
        </div>
      )}
      {items.length > 5 && (
        <div className="text-center mt-3 text-xs text-muted-foreground">+ {items.length - 5} más...</div>
      )}
    </div>
  );
}
